import { compact } from 'lodash'
import { ReactElement, useState } from 'react'
import { ActivityCard } from 'app/components/activities/ActivityCard'
import { TreatmentCategoryBadge } from 'app/components/TreatmentCategoryBadge'
import { useCdtCodeMappings } from 'app/hooks/useCdtCodeMappings'
import { getTreatmentCategories } from 'lib/cdtCodeDefaults'
import { TransactionTypeIcon } from 'app/components/activities/TransactionTypeIcon'
import type { TransactionJson } from 'app/types/entities'
import {
  carriesProcedureCategories,
  isLedgerReallocation,
  ledgerDaySummary,
  ledgerNetLabelForType,
  transactionLine,
} from 'lib/transactionCardContent'

/** The practice's CDT overrides, fetched once for the run and shared by its rows. */
type CdtMappings = ReturnType<typeof useCdtCodeMappings>[`mappings`]

/**
 * One row of the well: what it was on the left, what it came to on the right.
 *
 * The qualifier carries the distinction that matters inside a run, since three
 * payments on one day are usually not three of the same thing: one from
 * insurance, two from the patient. Procedures identify themselves by code
 * instead, having no verb to qualify.
 */
function LedgerRunRow({
  transaction,
  transfer,
  customMappings,
}: {
  transaction: TransactionJson
  transfer?: boolean
  customMappings: CdtMappings
}): ReactElement {
  const line = transactionLine(transaction)

  /*
   * Inside a transfer the ordinary payment wording is wrong: nothing was
   * received. The practice's own terms fit, and the data backs them up. On
   * Louisa's 2026-07-16 transfer every negative row carries a provider and
   * every positive row carries none, which is the shape of an OpenDental
   * income transfer: money is unapplied from where it sat and applied to a
   * provider, the two halves cancelling.
   *
   * Only said inside a detected transfer. A lone positive payment elsewhere may
   * be a refund, and the record does not say which, so it stays unlabelled
   * rather than guessed at.
   */
  const transferLabel = (transaction.amount ?? 0) > 0 ? `unapplied` : `applied`

  const descriptor = transfer
    ? transferLabel
    : line.kind === `amount`
      ? (line.qualifier ?? compact([transaction.procedureCode, transaction.procedureDescription]).join(` · `))
      : line.text

  /*
   * Beside the code rather than gathered on the card. A day of twenty
   * procedures would put twenty badges in the header, and a union stops saying
   * which procedure was which the moment a run holds more than one kind of work.
   */
  const categories =
    transfer || !carriesProcedureCategories(transaction.transactionType)
      ? []
      : getTreatmentCategories(compact([transaction.procedureCode]), customMappings)

  return (
    <li className="flex items-baseline justify-between gap-x-3 border-b border-subtle py-2 first:pt-0 last:border-b-0">
      <span className="flex min-w-0 items-center gap-x-1.5">
        {/* Blank when the PMS said nothing. A dash is filler that reads as data. */}
        <span className="min-w-0 truncate text-body-xs text-subtle">{descriptor}</span>
        {categories.map((category) => (
          <TreatmentCategoryBadge key={category} category={category} />
        ))}
      </span>
      {line.kind === `amount` && <span className="shrink-0 text-ui-sm font-semibold text-strong">{line.headline}</span>}
    </li>
  )
}

/**
 * How many rows a run shows before it asks. Past this the well stops being a
 * card and becomes a page: a heavy day runs to fourteen payments, which buries
 * whatever sits under it.
 */
const VISIBLE_ROWS = 5

/**
 * Several transactions of the same type on the same day, as one card.
 *
 * Three payments posted together are one event to a reader: the practice took
 * money once and the PMS split it across claims. The rows stay on the page
 * rather than folding away, because the reason to group them was noise, not
 * secrecy: five cards of chrome around five numbers is the noise, and the five
 * numbers are what someone came to read.
 */
export function LedgerRun({ transactions }: { transactions: TransactionJson[] }): ReactElement {
  const { mappings: customMappings } = useCdtCodeMappings()
  const [expanded, setExpanded] = useState(false)

  const summary = ledgerDaySummary(transactions)
  const type = transactions[0]?.transactionType
  const transfer = isLedgerReallocation(transactions)
  const net = transfer ? undefined : ledgerNetLabelForType(summary, type)

  /*
   * The total below always covers the whole run, so the button states how many
   * rows are held back rather than leaving a reader to wonder why five rows do
   * not add up to it.
   */
  const hidden = transactions.length - VISIBLE_ROWS
  const visible = expanded || hidden <= 0 ? transactions : transactions.slice(0, VISIBLE_ROWS)

  return (
    <ActivityCard
      icon={<TransactionTypeIcon type={type} />}
      hideChevronIcon
      title={
        <div className="flex flex-wrap items-center gap-x-1 text-ui-sm text-strong">
          <span>{`${transactions.length} ${type ? `${type}s` : `Entries`}`}</span>
          {transfer && <span className="text-body-xs text-subtle">moved between accounts</span>}
        </div>
      }
      preview={
        <div className="w-full rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          <ul className="flex flex-col">
            {visible.map((transaction) => (
              <LedgerRunRow
                key={transaction.id}
                transaction={transaction}
                transfer={transfer}
                customMappings={customMappings}
              />
            ))}
          </ul>
          {hidden > 0 && (
            <button
              type="button"
              className="mt-1 text-body-xs text-info-default hover:underline"
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded ? `Show less` : `Show ${hidden} more`}
            </button>
          )}
          <div className="-mx-4 -mb-3 mt-2 flex items-baseline justify-between gap-x-3 rounded-b-lg bg-surface-subtle px-4 py-2">
            <span className="text-body-xs text-subtle">Total</span>
            <span className="shrink-0 text-ui-sm font-semibold text-strong">
              {net ?? (
                <span className="font-normal text-subtle">
                  {transfer ? `No money in or out` : `Amounts not fully recorded`}
                </span>
              )}
            </span>
          </div>
        </div>
      }
      isFirst
      isLast
    />
  )
}
